<div
  class="upload-card"
  [class.state-success]="hasDocuments()"
  [class.state-danger]="!hasDocuments() && !isOptional()"
  [class.state-neutral]="!hasDocuments() && isOptional()"
  [class.error]="hasError()"
>
  @if (hasDocuments()) {
    <div class="status-icon success">
      <i class="pi pi-check-circle"></i>
    </div>
  }

  <div class="card-header">
    <div class="icon-container">
      <i class="pi icon pi-file"></i>
    </div>

    <div class="text-container">
      <div class="title-row">
        <h3>{{ title() }}</h3>

        @if (tooltipKey()) {
          <app-directus-tooltip [tooltipKey]="tooltipKey()" />
        }
      </div>

      <p class="subtitle">{{ subtitle() }}</p>
    </div>
  </div>

  <div class="documents">
    @for (row of rows(); track row.document.id) {
      <div
        class="flex items-start justify-between p-4 rounded-xl bg-gray-900 border border-black hover:border-gray-500"
        [attr.data-testid]="'document-upload-card-row-' + row.document.id"
      >
        <div class="flex gap-4 overflow-hidden">
          <div class="mt-1">
            <i class="pi pi-file-pdf text-green-500 text-2xl"></i>
          </div>

          <div class="flex flex-col gap-1 overflow-hidden">
            <span class="text-base text-gray-100 font-semibold truncate">
              {{ row.document.fileName }}
            </span>

            <div class="flex items-center gap-2 text-xs text-gray-500">
              <span>{{ title() }}</span>
              @if (row.document.fileSizeKb !== undefined) {
                <span class="text-gray-700">•</span>
                <span>{{ row.document.fileSizeKb | appNumber: '1.0-1' }} KB</span>
              }
              @if (row.document.createdAt) {
                <span class="text-gray-700">•</span>
                <span>{{ row.document.createdAt | appDate }}</span>
              }
            </div>

            <div class="flex items-center gap-2 mt-1">
              @if (row.document.version) {
                <span
                  class="px-2 py-0.5 rounded-sm bg-gray-800 text-gray-200 text-[10px] font-bold"
                  [pTooltip]="'common.version' | transloco"
                  tooltipPosition="left"
                >
                  {{ row.document.version }}
                </span>
              }
              @if (row.hasStatus) {
                <span
                  class="px-2 py-0.5 rounded-sm bg-gray-800 text-[10px] font-bold"
                  [pTooltip]="'common.status' | transloco"
                >
                  {{ row.statusTranslations | translateDirectus }}
                </span>
              }
            </div>

            @if (documentDetails(); as details) {
              <ng-container
                [ngTemplateOutlet]="details"
                [ngTemplateOutletContext]="{$implicit: row.document}"
              />
            }
          </div>
        </div>

        <div class="flex flex-col items-end gap-0">
          @if (documentActions(); as actions) {
            <div class="flex items-center">
              <ng-container
                [ngTemplateOutlet]="actions"
                [ngTemplateOutletContext]="{$implicit: row.document}"
              />
            </div>
          }

          <div class="flex items-center">
            @if (row.previewable) {
              <p-button
                icon="pi pi-eye"
                [text]="true"
                severity="secondary"
                styleClass="p-1! h-8! w-8! hover:bg-[color-mix(in_srgb,var(--color-green),transparent_60%)]! transition-colors"
                [pTooltip]="'common.previewDocument' | transloco"
                tooltipPosition="left"
                (onClick)="previewDocument.emit(row.document.id)"
                [attr.data-testid]="'document-upload-card-preview-' + row.document.id"
              />
            }
            <p-button
              icon="pi pi-download"
              [text]="true"
              severity="secondary"
              styleClass="p-1! h-8! w-8! hover:bg-[color-mix(in_srgb,var(--color-blue),transparent_60%)]! transition-colors"
              [pTooltip]="'common.download' | transloco"
              (onClick)="downloadDocument.emit(row.document.id)"
              [attr.data-testid]="'document-upload-card-download-' + row.document.id"
            />
          </div>

          @if (!readOnly()) {
            <div class="flex items-center">
              <p-button
                icon="pi pi-pen-to-square"
                [text]="true"
                severity="secondary"
                styleClass="p-1! h-8! w-8! hover:bg-[color-mix(in_srgb,var(--color-blue),transparent_60%)]! transition-colors"
                [pTooltip]="'common.edit' | transloco"
                tooltipPosition="left"
                (onClick)="onEdit(row.document)"
                [attr.data-testid]="'document-upload-card-edit-' + row.document.id"
              />
              <p-button
                icon="pi pi-times"
                [text]="true"
                severity="secondary"
                styleClass="p-1! h-8! w-8! hover:bg-[color-mix(in_srgb,var(--color-red),transparent_60%)]! transition-colors"
                [pTooltip]="'common.delete' | transloco"
                (onClick)="onDelete(row.document)"
                [attr.data-testid]="'document-upload-card-delete-' + row.document.id"
              />
            </div>
          }
        </div>
      </div>
    }
  </div>

  @if (canUpload()) {
    <button
      type="button"
      class="upload-area"
      (click)="triggerUpload()"
      (dragover)="onDragOver($event)"
      (drop)="onFileDropped($event)"
      data-testid="document-upload-card-upload"
    >
      <div class="upload-content">
        <i class="pi pi-upload upload-icon"></i>

        <p class="upload-text">{{ 'common.clickToUpload' | transloco }}</p>

        <p class="upload-hint">{{ hint() }}</p>
      </div>

      <input
        type="file"
        #fileInput
        class="hidden"
        tabindex="-1"
        accept=".pdf"
        (change)="onFileSelected()"
        data-testid="document-upload-card-file-input"
      />
    </button>
  }
</div>
